import { useState } from "react";
import HeatmapCanvas from "./HeatmapCanvas";
import axios from "axios";
import { toast } from "react-toastify";

const baseUrl = process.env.REACT_APP_BASE_URL;

const Message = ({ text }) => (
    <div className="flex items-center justify-center h-[300px] text-gray-500 text-sm">
        {text}
    </div>
);

const Loader = () => (
    <div className="flex items-center justify-center h-[300px] text-gray-500 text-sm animate-pulse">
        Loading heatmap...
    </div>
);

export default function HeatmapView({ selectedOutlet, token }) {
    const today = new Date().toISOString().slice(0, 10);

    const [dateStart, setDateStart] = useState(today);
    const [dateEnd, setDateEnd] = useState(today);

    const [orderPoints, setOrderPoints] = useState(null);
    const [salesPoints, setSalesPoints] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);

    const loadHeatmaps = async () => {
        if (!selectedOutlet || !token) {
            toast.error("Select outlet first");
            return;
        }

        if (!dateStart || !dateEnd) {
            toast.error("Please select dates");
            return;
        }

        if (dateStart > dateEnd) {
            toast.error("Start date must be before end date");
            return;
        }

        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const [ordersRes, salesRes] = await Promise.all([
                axios.post(`${baseUrl}/heatmap/table-orders`, {
                    token,
                    outlet: selectedOutlet,
                    dateStart,
                    dateEnd,
                }),
                axios.post(`${baseUrl}/heatmap/table-sales`, {
                    token,
                    outlet: selectedOutlet,
                    dateStart,
                    dateEnd,
                }),
            ]);

            setOrderPoints(ordersRes.data.data || []);
            setSalesPoints(salesRes.data.data || []);
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to load heatmaps. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8 mt-2.5">
            <div className="flex items-center justify-between max-w-5xl mx-auto">
                <div className="flex-1 flex justify-start">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={dateStart}
                            onChange={e => setDateStart(e.target.value)}
                            className="border px-2 py-1 rounded text-sm"
                        />
                    </div>
                </div>

                <div className="flex-1 flex justify-center">
                    <button
                        onClick={loadHeatmaps}
                        disabled={loading}
                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-pink-100 text-pink-800 hover:bg-pink-200 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {loading ? "Loading..." : "View"}
                    </button>
                </div>

                <div className="flex-1 flex justify-end">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={dateEnd}
                            onChange={e => setDateEnd(e.target.value)}
                            className="border px-2 py-1 rounded text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Orders Heatmap */}
            <div className="space-y-2 p-4 shadow-lg border max-w-6xl mx-auto bg-white rounded-xl">
                <h3 className="text-center font-semibold text-gray-700 text-xl">
                    Table vs Number of Orders Heatmap
                </h3>

                {loading && <Loader />}

                {!loading && error && <Message text={error} />}

                {!loading && !error && !searched && (
                    <Message text="Please select start and end dates and click View to see heatmaps." />
                )}

                {!loading && !error && orderPoints?.length > 0 && (
                    <HeatmapCanvas points={orderPoints} />
                )}
            </div>

            {/* Sales Heatmap */}
            <div className="space-y-2 p-4 shadow-lg border max-w-6xl mx-auto bg-white rounded-xl">
                <h3 className="text-center font-semibold text-gray-700 text-xl">
                    Table vs Sales Heatmap
                </h3>

                {loading && <Loader />}

                {!loading && error && <Message text={error} />}

                {!loading && !error && !searched && (
                    <Message text="Please select start and end dates and click View to see heatmaps." />
                )}

                {!loading && !error && salesPoints?.length > 0 && (
                    <HeatmapCanvas points={salesPoints} />
                )}
            </div>
        </div>
    );
}
